// src/main.tsx
import * as React from "react";
import { useEffect } from "react";
import * as ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { ThemeProvider } from "@/components/theme-provider";
import { router } from "./Router";
import "./index.css";
import { db } from "@/services/db";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { FontProvider } from "./contexts/font-context";
import { setupChunkErrorHandler } from "@/utils/handle-chunk-errors";
import { DevMemberProvider } from "./dev/DevMemberContext";
import { OnboardingInspectorProvider } from "./dev/OnboardingInspectorContext";
import { OnboardingInspector } from "./dev/OnboardingInspector";
import { initAnalytics, identifyUser } from "@/lib/analytics";
import { useUser } from "@clerk/clerk-react";

// Set up chunk error handler to auto-reload on chunk loading failures after deployment
setupChunkErrorHandler();

// Initialize PostHog analytics
initAnalytics();

// Environment detection
const isDev =
  import.meta.env.MODE === "development" ||
  window.location.hostname.includes("replit.dev");

// Get the correct key based on environment
const CLERK_KEY = isDev
  ? import.meta.env.VITE_CLERK_DEV_KEY
  : import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_KEY) {
  throw new Error(
    `Missing Clerk Key for ${isDev ? "development" : "production"}`,
  );
}

// Initialize app data - lightweight version
async function initializeAppData() {
  try {
    // Only ensure sample account exists (metadata only, no data loading)
    await db.ensureSampleAccount();

    // Sample data will be loaded lazily when user selects it from dropdown
    console.log("App initialized - sample data will load on demand");
  } catch (err) {
    console.error("Failed to initialize app data:", err);
  }
}

// Identify PostHog user from Clerk
function PostHogIdentifier() {
  const { user } = useUser();
  useEffect(() => {
    if (user) {
      identifyUser(user.id, {
        email: user.primaryEmailAddress?.emailAddress || "",
        name: user.fullName || "",
      });
    }
  }, [user]);
  return null;
}

function AppProviders({ children }: { children: React.ReactNode }) {
  // Initialize on mount
  useEffect(() => {
    initializeAppData();
  }, []);

  return (
    <DevMemberProvider>
      <ClerkProvider
        publishableKey={CLERK_KEY}
        appearance={{
          elements: {
            rootBox: "mx-auto w-full max-w-[440px] mt-[-200px]",
            card: "shadow-none",
            formButtonPrimary: "bg-primary hover:bg-primary/90",
            footerAction: "font-normal",
            formField: "gap-1",
            form: "gap-4",
            formFieldRow: "gap-3",
          },
          layout: {
            socialButtonsPlacement: "bottom",
            socialButtonsVariant: "iconButton",
            privacyPageUrl: "/privacy",
            termsPageUrl: "/terms",
          },
        }}
      >
        <PostHogIdentifier />
        <OnboardingInspectorProvider>
          <ThemeProvider defaultTheme="light">
            <FontProvider>
              <TooltipProvider>
                {children}
                {/* Dev-only Onboarding Inspector panel */}
                <OnboardingInspector />
              </TooltipProvider>
            </FontProvider>
          </ThemeProvider>
        </OnboardingInspectorProvider>
      </ClerkProvider>
    </DevMemberProvider>
  );
}

const root = document.getElementById("root");

if (!root) {
  throw new Error("Root element not found!");
}

const reactRoot = ReactDOM.createRoot(root);

try {
  reactRoot.render(
    <React.StrictMode>
      <AppProviders>
        <RouterProvider router={router} />
        <Toaster />
      </AppProviders>
    </React.StrictMode>,
  );
} catch (err) {
  console.error("Main: Fatal error during render:", err);
}
